import { useCallback, useMemo, useState } from 'react'

import { useWorkflowSchema } from 'data/hooks/workflows/workflows'

import useDebounce from 'v2/ui/utils/useDebounce'

import { useWorkflowEditorContext } from './useWorkflowEditorContext'

export type NodeGroup = WorkflowSchemaGroup & {
    nodes: WorkflowSchemaNodeType[]
}

const SEARCH_QUERY_DEBOUNCE_TIME = 500 // ms

export function useWorkflowNodeListState() {
    const { data: schema, isError, isLoading } = useWorkflowSchema()

    const { addNode, workflow } = useWorkflowEditorContext()

    const allowReturnData = !!schema?.triggers.find((t) => t.id === workflow.trigger.trigger_type)
        ?.allow_return_data

    const [searchQuery, setSearchQuery] = useState('')
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')
    const updateDebouncedSearchQuery = useDebounce(
        setDebouncedSearchQuery,
        SEARCH_QUERY_DEBOUNCE_TIME
    )

    const onSearchQueryChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value
            setSearchQuery(value)
            updateDebouncedSearchQuery(value.toLowerCase().trim())
        },
        [updateDebouncedSearchQuery]
    )

    const groups: NodeGroup[] = useMemo(() => {
        const nodes = schema?.nodes ?? []
        const schemaGroups = schema?.groups ?? []

        const filteredNodes = nodes.filter((n) => {
            if (n.status === 'deprecated') return false
            if (n.id === 'return_data' && !allowReturnData) return false

            if (debouncedSearchQuery) {
                return n.name.toLowerCase().includes(debouncedSearchQuery)
            }

            return true
        })

        // If there is a search query, we just return one group with all the nodes.
        if (!!debouncedSearchQuery) {
            return [
                {
                    id: 'all',
                    name: 'All',
                    color_scheme: 'Indigo',
                    order: 0,
                    nodes: filteredNodes,
                },
            ]
        }

        return mapNodesToGroups(filteredNodes, schemaGroups)
    }, [schema?.nodes, schema?.groups, allowReturnData, debouncedSearchQuery])

    const canSearch = !isError && !isLoading

    const totalNodesLength = groups.reduce((acc, group) => acc + group.nodes.length, 0)

    const effectiveIsError = isError && !isLoading
    const isEmpty = totalNodesLength < 1 && !debouncedSearchQuery && !effectiveIsError
    const isEmptySearch = totalNodesLength < 1 && !!debouncedSearchQuery && !effectiveIsError

    const onClearSearch = useCallback(() => {
        setSearchQuery('')
        setDebouncedSearchQuery('')
    }, [])

    return {
        searchQuery,
        setSearchQuery,
        canSearch,
        groups,
        addNode,
        onSearchQueryChange,
        isEmpty,
        isEmptySearch,
        isError: effectiveIsError,
        onClearSearch,
    }
}

function mapNodesToGroups(
    nodes: WorkflowSchemaNodeType[],
    schemaGroups: WorkflowSchemaGroup[]
): NodeGroup[] {
    const schemaGroupsById = new Map(schemaGroups.map((g) => [g.id, g]))

    const groups = nodes.reduce((groups, node) => {
        const groupId = node.group ?? ''
        const schemaGroup = schemaGroupsById.get(groupId)

        if (!groups.has(groupId) && schemaGroup) {
            groups.set(groupId, {
                ...schemaGroup,
                nodes: [],
            })
        }

        groups.get(groupId)!.nodes.push(node)

        return groups
    }, new Map<string, NodeGroup>())

    // Sort groups by order.
    return Array.from(groups.values()).sort((a, b) => a.order - b.order)
}
